<script setup>
import { debounce } from '@/composables/helpers/performance';

const props = defineProps({
    page: {
        type: Object,
        required: false,
    },
    headline: {
        type: String,
        required: false,
        default: 'Related Blog Posts',
    },
    button: {
        type: Object,
        required: false,
        default: {},
    },
    blog_posts: {
        type: Array,
        required: false,
        default: [],
    },
    backgroundColor: {
        type: String,
        required: false,
        default: 'var(--Chapati-500)',
    },
    attributes: {
        type: Object,
        required: false
    }
});

const active = ref(0)
const scrollContainer = ref(null)

const query = computed(() => {
    const ids = props.blog_posts?.length ? props.blog_posts.map(item => item.post.id) : []
    const params = { include: ids, limit: 3 }
    if (props.page?.category) params.category = props.page.category
    return params
})

const { data, pending, error, refresh } = useLazyAsyncData('blogs:featured', () => $fetch('/api/v1/posts', { params: query.value }))

const router = useRouter();

const handleCardClick = (payload) => {
    if (payload.type === 'tag') {
        router.push({ path: '/blog', query: { tags: payload.value.toLowerCase() } });
    }
};

const next = () => {
    active.value = active.value < (data.value?.length ?? 1) - 1 ? active.value + 1 : 0
    jump(active.value)
}

const previous = () => {
    active.value = active.value > 0 ? active.value - 1 : 0
    jump(active.value)
}

const check = () => {
    if (!scrollContainer.value) return
    const children = scrollContainer.value.children;
    let leftmostElement = null;
    let leftmostPosition = Infinity;
    for (let child of children) {
        const rect = child.getBoundingClientRect();
        const containerRect = scrollContainer.value.getBoundingClientRect();
        if (rect.right > containerRect.left && rect.left < containerRect.right) {
            if (rect.left < leftmostPosition) {
                leftmostPosition = rect.left;
                leftmostElement = child;
            }
        }
    }

    if (leftmostElement) {
        active.value = parseInt(leftmostElement.getAttribute('data-index'), 10)
    }
}

const jump = (index) => {
    if (!scrollContainer.value) return
    const target = scrollContainer.value.querySelector(`li[data-index="${index}"]`)
    if (target) {
        active.value = index;
        const offsetLeft = target.offsetLeft - scrollContainer.value.offsetLeft;
        scrollContainer.value.style.scrollSnapType = 'none'
        setTimeout(() => { scrollContainer.value.style.scrollSnapType = 'x mandatory' }, 1000)
        scrollContainer.value.scrollTo({
            left: offsetLeft,
            behavior: 'smooth'
        });
    }
}

const debounceScroll = debounce(check, 100)

onMounted(() => {
    if (process.server) return;
    if (scrollContainer.value) scrollContainer.value.addEventListener('scroll', debounceScroll)
})

onUnmounted(() => {
    if (process.server) return;
    if (scrollContainer.value) scrollContainer.value.removeEventListener('scroll', debounceScroll)
})

</script>

<template>
    <section class="block-blog-three-up" tabindex="-1" :class="props.page ? 'is-post bg-[var(--pink)] text-[var(--magenta)]' : 'text-white'"
        :style="!props.page ? 'background: linear-gradient(116deg, var(--orange, #D63D00) 0%, var(--purple, #820CA7) 100%)' : ''">
        <div class="px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)] py-(--box-padding-tb-m) lg:py-(--box-padding-tb)">
            <div class="header flex w-full items-center mb-[calc(var(--box-padding-tb)/2)]">
                <MessHtml tag="h2" class="headline font-hubspot-serif font-normal py-(--box-padding-tb-m)" :html="headline" />
                <a v-if="button?.url" :href="button.url" :title="button.title"
                    :class="['ml-auto hidden lg:inline-flex items-center gap-2.5 px-5 py-2.5 rounded-[50px] font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] text-[var(--magenta)] no-underline transition-colors duration-200 hover:bg-[var(--magenta)] hover:text-white focus-visible:outline-none focus-visible:ring-2',
                        page ? 'bg-transparent border border-[var(--magenta)] focus-visible:ring-[var(--magenta)]' : 'bg-white focus-visible:ring-white']">
                    {{ button.label }}
                </a>
            </div>
            <div class='posts-container' v-if="data && data.length">
                <div class="posts mb-[calc(var(--box-padding-tb)*2)]">
                    <ul class="flex overflow-x-scroll gap-(--box-padding-tb) snap-x snap-mandatory" ref="scrollContainer">
                        <li v-for="(card, index) in data" :key="card.data.url" :data-index=index class="snap-start shrink-0 flex-[1_1_100%] max-w-full lg:flex-none lg:w-[calc((100%-2*var(--box-padding-tb))/3)] lg:max-w-none">
                            <article>
                                <FragmentsCardsBlogCard class="post" :link="card.data.url" :headline="card.data.title"
                                    :cardImage="card.data.image" :altText="card.data.imageAltText"
                                    :category="card.data.category" :date="card.data.datePublished" :excerpt="card.data.excerpt"
                                    :tags="card.data.tags" @clicked="handleCardClick" tagColor="white-outline"
                                    size="medium" />
                            </article>
                        </li>
                    </ul>
                </div>
                <div class="controls lg:hidden flex items-center gap-[16px]">
                    <div :class="['flex gap-[16px] items-center justify-center px-[16px] py-[7px] h-[42px] rounded-[40px] border', page ? 'border-[var(--magenta)]' : 'border-white']">
                        <button type="button" aria-label="Previous post"
                            :disabled="active === 0"
                            :class="['group shrink-0 size-[15px] bg-transparent border-none p-0 focus-visible:outline-none focus-visible:ring-2 rounded-sm transition-opacity duration-200', active === 0 ? 'opacity-30 cursor-default' : 'cursor-pointer']"
                            @click="previous">
                            <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                                <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                                    :stroke="page ? 'var(--magenta)' : 'white'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <button type="button" aria-label="Next post"
                            :disabled="active === (data?.length ?? 1) - 1"
                            :class="['group shrink-0 size-[15px] bg-transparent border-none p-0 -scale-x-100 focus-visible:outline-none focus-visible:ring-2 rounded-sm transition-opacity duration-200', active === (data?.length ?? 1) - 1 ? 'opacity-30 cursor-default' : 'cursor-pointer']"
                            @click="next">
                            <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                                <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                                    :stroke="page ? 'var(--magenta)' : 'white'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <a v-if="button?.url" :href="button.url" :title="button.title"
                        :class="['inline-flex items-center gap-2.5 px-5 py-2.5 rounded-[50px] border bg-transparent font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] no-underline transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2',
                            page ? 'border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white focus-visible:ring-[var(--magenta)]' : 'border-white text-white hover:bg-white hover:text-[var(--magenta)] focus-visible:ring-white']">
                        {{ button.label }}
                    </a>
                </div>
            </div>
        </div>
    </section>
</template>

<style>
/* is-post inherits the inline gradient — no override needed */

/* [pseudo] no-text-crop on headline */
.block-blog-three-up .header .headline::before,
.block-blog-three-up .header .headline::after {
    content: none;
}

/* [vendor] hide scrollbar */
.block-blog-three-up .posts ul {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

/* [vendor] hide scrollbar webkit */
.block-blog-three-up .posts ul::-webkit-scrollbar {
    display: none;
}

/* [child] dot controls alignment */
.block-blog-three-up .controller .dot-controls {
    margin-left: auto;
}

/* [child] arrow svg fill */
.block-blog-three-up .controller .back-next .next svg path,
.block-blog-three-up .controller .back-next .previous svg path {
    fill: var(--teal);
}

/* [override] category tag: orange bg, white text, no border (gradient version only) */
.block-blog-three-up:not(.is-post) .blog-card .details-container .project-tag,
.block-blog-three-up:not(.is-post) .blog-card .details-container .project-tag p {
    color: white;
    background: var(--orange);
    border: none;
}

.block-blog-three-up:not(.is-post) .blog-card .details-container .project-tag:hover,
.block-blog-three-up:not(.is-post) .blog-card .details-container .project-tag:focus {
    background: var(--orange);
    color: white;
}

/* [override] tag pills: white text/border, orange bg on hover (gradient version only) */
.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag,
.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag p {
    color: white;
    border-color: white;
}

.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag:hover,
.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag:focus {
    background: var(--orange);
    border-color: var(--orange);
    color: white;
}

.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag:hover p,
.block-blog-three-up:not(.is-post) .blog-card .tag-container .project-tag:focus p {
    color: white;
}

/* [override] headline hover stays white (gradient version only) */
.block-blog-three-up:not(.is-post) .blog-card .headline:hover {
    color: white !important;
    text-decoration-color: white !important;
}

/* [override] is-post: magenta text, no border on category tag */
.block-blog-three-up.is-post .blog-card .headline,
.block-blog-three-up.is-post .blog-card .excerpt,
.block-blog-three-up.is-post .blog-card .date {
    color: var(--magenta);
}

.block-blog-three-up.is-post .blog-card .details-container .project-tag {
    border: none;
}
</style>
